import React from 'react'
import differenceInDays from 'date-fns/differenceInDays';
import ApiPaymentInfo from '../code/api/ApiPaymentInfo';
import { BillingButtonBarButton } from '../code/enums/Enums';
import Customer from '../code/models/Customer';
import PaymentInfo from '../code/models/PaymentInfo';
import { stringToDate } from '../code/helpers/stringToDate';
import CclDataTable, { CclDataTableColumnDef as CclDataTableColumnDef, CclDataTableColumnType } from './CclDataTable';
import CustomerInfoBillingInfoButtonBar from './CustomerInfoBillingInfoButtonBar';
import AuthAdminUserModel from '../code/models/AuthAdminUserModel';
import ButtonConfirm, { ButtonConfirmSize } from './ButtonConfirm';
import Api_Config from '../code/api/ApiConfig';
import { formatCurrency } from '../code/helpers/numberFormatting';
import '../css/customerinfobillinginfo.css';

interface ICustomerInfoBillingInfo {
    customer: Customer;
    currentUser: AuthAdminUserModel;
    showToast: (message: string) => void;
}

export default function CustomerInfoBillingInfo({ customer, currentUser, showToast }: ICustomerInfoBillingInfo) {
    const [paymentInfo, setPaymentInfo] = React.useState<PaymentInfo | null>(null);
    const [selectedPaymentsRow, setSelectedPaymentsRow] = React.useState<any>();
    const [warningMessage, setWarningMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
    const [showTransactionHistory, setShowTransactionHistory] = React.useState<boolean>(false);
    const company = Api_Config.getSelectedCompany();

    React.useEffect(() => {
        loadPaymentInfo();
    }, []);

    async function loadPaymentInfo() {
        setSelectedPaymentsRow(null);

        ApiPaymentInfo.getPaymentInfo(customer.customerId).then(data => {
            setPaymentInfo(data);
        });
    }

    function handleOnButtonClickEvent(button: BillingButtonBarButton, data: any) {
        switch (button) {
            case BillingButtonBarButton.CreateCharge:
                doCreateCharge(data);
                break;
            case BillingButtonBarButton.CreateCredit:
                doCreateCredit(data);
                break;
            case BillingButtonBarButton.CreatePromotion:
                doCreatePromotion(data);
                break;
            case BillingButtonBarButton.ProcessPaymets:
                doProcessPayments();
                break;
            case BillingButtonBarButton.TransactionHistory:
                setShowTransactionHistory(!showTransactionHistory);
                break;
        }
    }

    function postSuccessMessage(msg: string): void {
        setSuccessMessage(msg);
        setTimeout(() => { setSuccessMessage(null); }, 4000);
    }

    function postWarningMessage(msg: string) {
        setWarningMessage(msg);
        setTimeout(() => { setWarningMessage(null); }, 8000);
    }

    function doCreateCharge(data: any) {
        ApiPaymentInfo.processCharge(customer.customerId, data.amount, data.description).then(success => {
            if (success) {
                postSuccessMessage("Charge created for customer.");
                loadPaymentInfo();
            } else {
                postWarningMessage("Unable to add charge for customer.");
            }
        });
    }

    function doCreateCredit(data: any) {
        ApiPaymentInfo.processCredit(customer.customerId, data.amount, data.description).then(success => {
            if (success) {
                postSuccessMessage("Credit created for customer.");
                loadPaymentInfo();
            } else {
                postWarningMessage("Unable to add credit for customer.");
            }
        });
    }

    function doCreatePromotion(data: any) {
        postWarningMessage("Create Promotion Not Implemented.");
    }

    function doProcessPayments() {
        ApiPaymentInfo.processPayments(customer.customerId).finally(() => {
            loadPaymentInfo();
            postSuccessMessage("Process payments completed.");
        });
    }

    function formatCurrencyValue(val: any): string {
        return formatCurrency(val, company.currency);
    }

    function formatDate(val: any): string {
        return `${stringToDate(val + "")}`;
    }

    function formatCheck(val: any) {
        return (
            <input type="checkbox" readOnly checked={val} />
        )
    }

    const chargesColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "id",
            title: "id",
            visible: false,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "description",
            title: "Description",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "createDate",
            title: "Date",
            visible: true,
            formatData: formatDate,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "amount",
            title: "Amount",
            visible: true,
            formatData: formatCurrencyValue,
            type: CclDataTableColumnType.Data
        }
    ];

    const creditsColumnDefs = [
        {
            fieldName: "id",
            title: "id",
            visible: false,
            formatData: null,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "description",
            title: "Description",
            visible: true,
            formatData: null,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "amount",
            title: "Amount",
            visible: true,
            formatData: formatCurrencyValue,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "balance",
            title: "Balance",
            visible: true,
            formatData: formatCurrencyValue,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "updateDate",
            title: "Updated",
            visible: true,
            formatData: formatDate,
            type: CclDataTableColumnType.Data
        }
    ];

    const paymentsColumnDefs = [
        {
            fieldName: "id",
            title: "id",
            visible: false,
            formatData: null
        },
        {
            fieldName: "amount",
            title: "Amount",
            visible: true,
            formatData: formatCurrencyValue
        },
        {
            fieldName: "paymentType",
            title: "Type",
            visible: true,
            formatData: null
        },
        {
            fieldName: "paymentDate",
            title: "Date",
            visible: true,
            formatData: formatDate
        },
        {
            fieldName: "refunded",
            title: "Refunded",
            visible: true,
            formatData: formatCheck
        }
    ]

    const promotionsColumnDefs = [
        {
            fieldName: "code",
            title: "Code",
            visible: true,
            formatData: null
        },
        {
            fieldName: "discount",
            title: "Discount",
            visible: true,
            formatData: null
        },
        {
            fieldName: "month",
            title: "Month",
            visible: true,
            formatData: null
        },
        {
            fieldName: "applied",
            title: "Applied",
            visible: true,
            formatData: formatCheck
        }
    ];

    const txHistColumnDefs = [
        {
            fieldName: "transactionType",
            title: "Type",
            visible: true,
            formatData: null,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "transactionId",
            title: "Id",
            visible: true,
            formatData: null,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "transactionDate",
            title: "Date",
            visible: true,
            formatData: formatDate,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "paymentSystemName",
            title: "Processor",
            visible: true,
            formatData: null,
            type: CclDataTableColumnType.Data,

        },
        {
            fieldName: "transactionData",
            title: "Ext Data",
            visible: true,
            className: "tx-data-column",
            type: CclDataTableColumnType.Data
        }
    ];

    function buildDataTable(
        title: string,
        data: any,
        colDefs: any,
        rowSelect: boolean,
        onRowSelect: ((rowData: any) => void) | null,
        getExtraDOM: (() => JSX.Element) | null,
        wideMode: boolean = false,
        tallMode: boolean = false) {

        let cssClasses = "col-md-4 max-height-300";
        cssClasses += wideMode ? " max-width-default" : " min-width-default";
        cssClasses += tallMode ? " max-height-600" : " max-height-300";

        return (
            <div id={title.toLowerCase() + "TableMain"} className={cssClasses}>
                <div className='table-title'>
                    <span className='table-title-span'>{title}</span>
                    {getExtraDOM && getExtraDOM()}
                </div>
                <div id={title.toLowerCase() + "TableWrapper"} className="col-md-12 table-wrapper">
                    {data &&
                        <CclDataTable
                            name={title.toLowerCase() + "Table"}
                            tableData={data}
                            columnDefinitions={colDefs}
                            rowSelect={rowSelect}
                            onRowSelect={onRowSelect}
                            onButtonClick={null} />}
                </div>
            </div>
        )
    }

    function onPaymentsRowSelect(rowData: any) {
        setSelectedPaymentsRow(rowData);
    }

    function handlePaymentsRefund(doRefund: boolean) {
        if (doRefund) {
            ApiPaymentInfo.refundPayment(selectedPaymentsRow.id).then(res => {
                if (res) {
                    showToast("Payment successfully refunded.");
                } else {
                    showToast("Payment was unable to be refunded.");
                }

                loadPaymentInfo();
            });
        }
    }

    function isWithinWindow(paymentDate: string): boolean {
        let rowDate = new Date(`${paymentDate}Z`);
        let nowDate = new Date();
        var diff = Math.abs(differenceInDays(nowDate, rowDate));
        return diff <= 90;
    }

    function getPaymentsExtraDOM(): JSX.Element {
        return (
            <React.Fragment>
                {currentUser.canDoBilling && !!selectedPaymentsRow && selectedPaymentsRow.paymentType === "CC" &&
                    isWithinWindow(selectedPaymentsRow.paymentDate) && !selectedPaymentsRow.refunded &&
                    <ButtonConfirm
                        enabled={true}
                        buttonText="Refund"
                        confirmTitle="Refund"
                        confirmMessage={`Refund payment ${formatCurrency(selectedPaymentsRow.amount, company.currency)} collected on ${formatDate(selectedPaymentsRow.paymentDate)}?`}
                        onConfirm={handlePaymentsRefund}
                        buttonSize={ButtonConfirmSize.Small}
                        cssClass="table-title-button" />}
            </React.Fragment>
        )
    }

    function renderTransactionHistory() {
        return (
            <React.Fragment>
                {buildDataTable("Transaction History", !!paymentInfo ? paymentInfo.transactionHistory : null,
                    txHistColumnDefs, false, null, null, true, true)}
            </React.Fragment>
        )
    }

    function renderFinancialTables() {
        return (
            <React.Fragment>
                {buildDataTable("Charges", !!paymentInfo ? paymentInfo.charges : null, chargesColumnDefs, false, null, null)}
                {buildDataTable("Credits", !!paymentInfo ? paymentInfo.discounts : null, creditsColumnDefs, false, null, null)}
                {buildDataTable("Payments", !!paymentInfo ? paymentInfo.payments : null, paymentsColumnDefs, true, onPaymentsRowSelect, getPaymentsExtraDOM)}
                {buildDataTable("Promotions", !!paymentInfo ? paymentInfo.promotions : null, promotionsColumnDefs, false, null, null)}
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            {!!successMessage && <div className="alert alert-success alert-dismissible" role="alert">
                <div>{successMessage}</div>
                <button type="button" className="btn-close" aria-label="Close"
                    onClick={() => { setSuccessMessage(null); }}></button>
            </div>}
            {!!warningMessage && <div className="alert alert-warning alert-dismissible" role="alert">
                <div>{warningMessage}</div>
                <button type="button" className="btn-close" aria-label="Close"
                    onClick={() => { setWarningMessage(null); }}></button>
            </div>}
            {paymentInfo && <CustomerInfoBillingInfoButtonBar currentUser={currentUser} paymentInfo={paymentInfo}
                showTransactionHistory={showTransactionHistory} onButtonClickEvent={handleOnButtonClickEvent} />}
            <div className="customer-billing-info" aria-readonly={true}>
                {showTransactionHistory ? renderTransactionHistory() : renderFinancialTables()}
            </div>
        </React.Fragment>
    )
}

